import {
  debounce,
  TextField,
  FormControl,
  InputLabel,
  NativeSelect,
} from "@mui/material";
import {
  GridFilterInputValueProps,
  GridFilterOperator,
  GridFilterItem,
} from "@mui/x-data-grid-pro";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { dealerTzToUtcDate } from "../../services/utilities/utility";

function CustomFilterInputValue(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;
  const customFilterRef: React.Ref<any> = React.useRef(null);
  const [inputValue, setInputValue] = React.useState(item.value || 0);

  const handleSearchTextChange = React.useCallback(
    debounce((newValue: string) => {
      applyValue({ ...item, value: newValue });
    }, 1000),
    [applyValue, item],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    const numericValue = parseFloat(newValue);
    if (numericValue < 0) {
      return;
    }
    setInputValue(newValue);
    handleSearchTextChange(newValue);
  };

  React.useEffect(() => {
    setInputValue(item.value || "");
  }, [item.value]);
  return (
    <TextField
      name="mfrFilterValue"
      onChange={handleInputChange}
      ref={customFilterRef}
      size="small"
      value={inputValue}
      variant="standard"
      label="Value"
      type="number"
      inputProps={{ min: 0 }}
      sx={{ pt: 0.4 }}
    ></TextField>
  );
}

function BooleanFilterInput(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const handleFilterChange = (event: any) => {
    applyValue({
      ...item,
      value: event.target.value !== "Select" ? event.target.value : undefined,
    });
  };

  return (
    <FormControl fullWidth>
      <InputLabel variant="standard" shrink>
        Value
      </InputLabel>
      <NativeSelect
        value={item.value ?? ""}
        onChange={handleFilterChange}
        inputProps={{
          name: "boolean-filter",
        }}
      >
        <option value="" disabled>
          Select
        </option>
        <option value="true">Enabled</option>
        <option value="false">Disabled</option>
      </NativeSelect>
    </FormControl>
  );
}

function SyncTypeFilterInput(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const handleFilterChange = (event: any) => {
    applyValue({
      ...item,
      value: event.target.value !== "Select" ? event.target.value : undefined,
    });
  };

  return (
    <FormControl fullWidth>
      <InputLabel variant="standard" shrink>
        Sync Type
      </InputLabel>
      <NativeSelect
        value={item.value ?? ""}
        onChange={handleFilterChange}
        inputProps={{
          name: "sync-type-filter",
        }}
      >
        <option value="" disabled>
          Select
        </option>
        <option value="true">Reverse</option>
        <option value="false">Regular</option>
      </NativeSelect>
    </FormControl>
  );
}

function MatchingTypeFilterInput(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const handleFilterChange = (event: any) => {
    applyValue({
      ...item,
      value: event.target.value !== "Select" ? event.target.value : undefined,
    });
  };

  return (
    <FormControl fullWidth>
      <InputLabel variant="standard" shrink>
        Matching Type
      </InputLabel>
      <NativeSelect
        value={item.value ?? ""}
        onChange={handleFilterChange}
        inputProps={{
          name: "matching-type-filter",
        }}
      >
        <option value="" disabled>
          Select
        </option>
        <option value="Catalog">Catalog</option>
        <option value="DMS">DMS</option>
        <option value="Custom">Custom</option>
      </NativeSelect>
    </FormControl>
  );
}

function LocationFilterInput(
  props: GridFilterInputValueProps & {
    locationOptions?: Array<{ id: number | string; name: string }>;
  },
) {
  const { item, applyValue, locationOptions = [] } = props;

  const handleFilterChange = (event: any) => {
    applyValue({
      ...item,
      value: event.target.value !== "" ? event.target.value : undefined,
    });
  };

  return (
    <FormControl fullWidth>
      <InputLabel variant="standard" shrink>
        Location
      </InputLabel>
      <NativeSelect
        value={item.value ?? ""}
        onChange={handleFilterChange}
        inputProps={{
          name: "location-filter",
        }}
      >
        <option value="" disabled>
          Select Location
        </option>
        {locationOptions.map((loc) => (
          <option key={loc.id} value={loc.id}>
            {loc.name}
          </option>
        ))}
      </NativeSelect>
    </FormControl>
  );
}

function DateFilterInput(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const handleDateChange = (newValue: Dayjs | null) => {
    applyValue({
      ...item,
      value: newValue ? newValue.toDate() : undefined,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MobileDateTimePicker
        value={item.value ? dayjs(item.value) : null}
        onChange={handleDateChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Value"
            variant="standard"
            size="small"
            fullWidth
            inputProps={{
              ...params.inputProps,
              style: {
                ...(params.inputProps?.style || {}),
                paddingBottom: 5,
                paddingTop: 4,
              },
            }}
            sx={{
              minWidth: 0,
              "& .MuiInputBase-root": {
                overflow: "hidden",
                minWidth: 0,
              },
              "& .MuiInputBase-input": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiInputAdornment-root .MuiIconButton-root": {
                marginRight: 0,
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
}

export const customPriceFilterOperators: GridFilterOperator[] = [
  {
    label: "Equals",
    value: "equals",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.columnField ||
        !filterItem.value ||
        !filterItem.operatorValue
      ) {
        return null;
      }

      return (params): boolean => {
        return params.value == filterItem.value;
      };
    },
    InputComponent: CustomFilterInputValue,
  },
  {
    label: "Less than",
    value: "lessThan",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.columnField ||
        !filterItem.value ||
        !filterItem.operatorValue
      ) {
        return null;
      }

      return (params): boolean => {
        return params.value == filterItem.value;
      };
    },
    InputComponent: CustomFilterInputValue,
  },
  {
    label: "Greater than",
    value: "greaterThan",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.columnField ||
        !filterItem.value ||
        !filterItem.operatorValue
      ) {
        return null;
      }

      return (params): boolean => {
        return params.value == filterItem.value;
      };
    },
    InputComponent: CustomFilterInputValue,
  },
];

export const booleanFilterOperators: GridFilterOperator[] = [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) {
        return null;
      }
      return (params): boolean => {
        const itemValue = filterItem.value === "true";
        return params.value === itemValue;
      };
    },
    InputComponent: BooleanFilterInput,
  },
];

export const syncTypeFilterOperators: GridFilterOperator[] = [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) {
        return null;
      }
      return (params): boolean => {
        const itemValue = filterItem.value === "true";
        return params.value === itemValue;
      };
    },
    InputComponent: SyncTypeFilterInput,
  },
];

export const createLocationFilterOperators = (
  locations: Array<{ id: number | string; name: string }>,
): GridFilterOperator[] => [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) {
        return null;
      }
      return (params): boolean => {
        return params.value === filterItem.value;
      };
    },
    InputComponent: LocationFilterInput as any,
    InputComponentProps: { locationOptions: locations },
  },
];

export const matchingTypeFilterOperators: GridFilterOperator[] = [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.value) {
        return null;
      }
      return (params): boolean => {
        return params.value === filterItem.value;
      };
    },
    InputComponent: MatchingTypeFilterInput,
  },
];

export const dateFilterOperators = (dealerTz: string): GridFilterOperator[] => [
  {
    label: "Is",
    value: "equals",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const filterDateUtc = dealerTzToUtcDate(filterItem.value, dealerTz);
      if (!filterDateUtc) return null;
      return (params): boolean => {
        const cellDateUtc = dealerTzToUtcDate(params.value, dealerTz);
        return cellDateUtc
          ? cellDateUtc.getTime() === filterDateUtc.getTime()
          : false;
      };
    },
    InputComponent: DateFilterInput,
  },
  {
    label: "Is not",
    value: "isNot",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const filterDateUtc = dealerTzToUtcDate(filterItem.value, dealerTz);
      if (!filterDateUtc) return null;
      return (params): boolean => {
        const cellDateUtc = dealerTzToUtcDate(params.value, dealerTz);
        return cellDateUtc
          ? cellDateUtc.getTime() !== filterDateUtc.getTime()
          : false;
      };
    },
    InputComponent: DateFilterInput,
  },
  {
    label: "After",
    value: "greaterThan",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const filterDateUtc = dealerTzToUtcDate(filterItem.value, dealerTz);
      if (!filterDateUtc) return null;
      return (params): boolean => {
        const cellDateUtc = dealerTzToUtcDate(params.value, dealerTz);
        return cellDateUtc ? cellDateUtc > filterDateUtc : false;
      };
    },
    InputComponent: DateFilterInput,
  },
  {
    label: "On or after",
    value: "onOrAfter",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const filterDateUtc = dealerTzToUtcDate(filterItem.value, dealerTz);
      if (!filterDateUtc) return null;
      return (params): boolean => {
        const cellDateUtc = dealerTzToUtcDate(params.value, dealerTz);
        return cellDateUtc ? cellDateUtc >= filterDateUtc : false;
      };
    },
    InputComponent: DateFilterInput,
  },
  {
    label: "Before",
    value: "lessThan",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const filterDateUtc = dealerTzToUtcDate(filterItem.value, dealerTz);
      if (!filterDateUtc) return null;
      return (params): boolean => {
        const cellDateUtc = dealerTzToUtcDate(params.value, dealerTz);
        return cellDateUtc ? cellDateUtc < filterDateUtc : false;
      };
    },
    InputComponent: DateFilterInput,
  },
  {
    label: "On or before",
    value: "onOrBefore",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      const filterDateUtc = dealerTzToUtcDate(filterItem.value, dealerTz);
      if (!filterDateUtc) return null;
      return (params): boolean => {
        const cellDateUtc = dealerTzToUtcDate(params.value, dealerTz);
        return cellDateUtc ? cellDateUtc <= filterDateUtc : false;
      };
    },
    InputComponent: DateFilterInput,
  },
  {
    label: "Is empty",
    value: "isEmpty",
    getApplyFilterFn:
      () =>
      (params): boolean =>
        dealerTzToUtcDate(params.value, dealerTz) === null,
  },
  {
    label: "Is not empty",
    value: "isNotEmpty",
    getApplyFilterFn:
      () =>
      (params): boolean =>
        dealerTzToUtcDate(params.value, dealerTz) !== null,
  },
];
